import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";

import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TrialProvider } from "./context/TrialContext";
import ProtectedRoute from "./pages/ProtectedRoute.jsx";
import AdminRoute from "./pages/AdminRoute.jsx";
import DefaultDirect from "./pages/DefaultRoute.jsx";
import SignUpPage from "./pages/SignUpPage.jsx";
import SignInPage from "./pages/SignInPage.jsx";
import VerifyAccountPage from "./pages/VerifyAccountPage.jsx";
import ForgotPasswordPage from "./pages/ForgotPasswordPage.jsx";
import ResetPasswordPage from "./pages/ResetPasswordPage.jsx";
import TemplateLibrary from "./components/template-library.jsx";
import { TEMPLATES_FEATURE_ENABLED } from "./context/FeatureFlagsContext";
import { SettingsHeader } from "./components/settings-header.jsx";
import MedicalScribeApp from "./components/medical/main-content";
import AuthCallback from "./components/auth/auth-callback.jsx";
import OtpVerifyPage from "./pages/OtpVerifyPage.jsx";
import TaskPage from "./components/task-settings.jsx";
import PaymentPage from "./pages/PaymentPage.jsx";
import PaymentReceipt from "./pages/PaymentReceipt.jsx";
import PaymentReturnPage from "./pages/PaymentReturnPage.jsx";
import OnboardingPage from "./pages/OnboardingPage.jsx";
import SupportTicketPage from "./pages/SupportTicketPage.jsx";
import GeneralFeedbackPage from "./pages/GeneralFeedbackPage.jsx";
import { ScribeDashboard } from "./components/scribe-tasks/ScribeDashboard.jsx";
import GlobalDashboard from "./components/admin/GlobalDashboard.jsx";
import AssignTask from "./components/scribe-tasks/AssingTask.jsx";
import { NotificationProvider } from "./context/NotificationContext";
import { SplitViewOverlay } from "./components/ui/SplitViewOverlay.jsx";
import ChatBotContainer from "./components/ui/chatBot-container.jsx";
import { PricingProvider } from "./context/PricingContext";
import { useAppVersionCheck } from "./hooks/useAppVersionCheck";
import ScrollToTop from "./components/ScrollToTop.jsx";
import { publicSiteUrl, isPublicSiteSelf, PRICING_PATH } from "./constants/publicSite";
import { TALK_TO_SALES_PATH, BOOKING_CONFIRMED_PATH } from "./constants/groupsPlan";

/**
 * Pages that live on the public marketing site (different domain).
 * Hitting these paths on the app domain forwards to the public site,
 * preserving any query string (e.g. UTM / plan params).
 *
 * If REACT_APP_PUBLIC_SITE_URL is misconfigured to this app's own origin,
 * forwarding would loop forever — bail out to the app root instead.
 */
const ExternalRedirect = ({ path }) => {
  const { search, hash } = useLocation();
  const selfLoop = isPublicSiteSelf();
  useEffect(() => {
    if (selfLoop) return;
    window.location.replace(`${publicSiteUrl(path)}${search || ""}${hash || ""}`);
  }, [path, search, hash, selfLoop]);
  return selfLoop ? <Navigate to="/" replace /> : null;
};

const HelpCenterArticleRedirect = () => {
  const { slug } = useParams();
  const selfLoop = isPublicSiteSelf();
  useEffect(() => {
    if (selfLoop) return;
    window.location.replace(
      slug ? publicSiteUrl(`/help-center/${slug}`) : publicSiteUrl("/help-center"),
    );
  }, [slug, selfLoop]);
  return selfLoop ? <Navigate to="/" replace /> : null;
};

const AppRoutes = () => {
  useAppVersionCheck();

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route element={<DefaultDirect />}>
          <Route path="/" element={<Navigate to="/signin" replace />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/verify-account" element={<VerifyAccountPage />} />
          <Route path="/verify" element={<VerifyAccountPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/otp-verify" element={<OtpVerifyPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Route>

        {/* Public Marketing & Legal Pages Redirects */}
        <Route path="/help-center" element={<ExternalRedirect path="/help-center" />} />
        <Route path="/help-center/:slug" element={<HelpCenterArticleRedirect />} />
        <Route path="/terms-of-use" element={<ExternalRedirect path="/terms-of-use" />} />
        <Route path="/privacy-policy" element={<ExternalRedirect path="/privacy-policy" />} />
        {/* Sales / booking flow lives on the public site — forward, don't 404 to /signin */}
        <Route path={TALK_TO_SALES_PATH} element={<ExternalRedirect path={TALK_TO_SALES_PATH} />} />
        <Route path={BOOKING_CONFIRMED_PATH} element={<ExternalRedirect path={BOOKING_CONFIRMED_PATH} />} />
        <Route path={PRICING_PATH} element={<ExternalRedirect path={PRICING_PATH} />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/rate-us" element={<GeneralFeedbackPage />} />
          <Route path="/support-ticket" element={<SupportTicketPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />

          {/* ---- SINGLE LAYOUT for all scribe and admin routes ---- */}
          <Route element={<MedicalScribeApp />}>
            {/* Scribe routes */}
            <Route path="scribe">
              <Route index element={<Navigate to="/scribe" />} /> 
              <Route path="dashboard" element={<ScribeDashboard />} />
              {TEMPLATES_FEATURE_ENABLED ? (
                <Route path="templates" element={<TemplateLibrary />} />
              ) : (
                <Route path="templates" element={<Navigate to="/scribe" replace />} />
              )}
              <Route path="settings" element={<SettingsHeader />} />
              <Route path="assign-task" element={<AssignTask />} />
              <Route path="task" element={<TaskPage />} />
            </Route>

            {/* Admin route – only for users with admin privileges */}
            <Route element={<AdminRoute />}>
              <Route path="admin/analytics" element={
                  <div className="p-4 sm:p-6 h-full overflow-y-auto">
                    <GlobalDashboard />
                  </div>
                }
                 />
            </Route>
          </Route>

          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/payment/return" element={<PaymentReturnPage />} />
          <Route path="/payment-receipt/:paymentId" element={<PaymentReceipt />} />
        </Route>

        {/* Catch-all redirect to /signin */}
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    </>
  );
};

const AppShell = () => {
  const location = useLocation();

  // All routes render at the browser's native 100% size (no global scaling).
  // The landing modifier is only a styling hook for marketing/auth pages.
  const isAppRoute = location.pathname.includes('/scribe') ||
                     location.pathname.includes('/admin');

  const isLandingRoute = !isAppRoute;

  return (
    <AuthProvider>
      <NotificationProvider>
        <PricingProvider>
          <TrialProvider>
            <Toaster position="top-center" reverseOrder={false} />
            <div className={`app-wrapper${isLandingRoute ? " app-wrapper--landing" : ""}`}>
              <AppRoutes />
            </div>
            <ChatBotContainer />
            <SplitViewOverlay />
          </TrialProvider>
        </PricingProvider>
      </NotificationProvider>
    </AuthProvider>
  );
};

const App = () => (
  <Router>
    <AppShell />
  </Router>
);

export default App;